/* eslint-disable no-alert */
/* eslint-disable no-console */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'
import { useFormik } from 'formik'
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material'
import { complement, isNil } from 'ramda'

import noop from 'src/utilities/noop'
import useSession from 'src/authentication/hooks/use-session'
import axios from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  selectClassID,
  selectCourseByID,
  selectCourseID,
  selectCoursesStatus,
  fetchCourses,
} from './classroom.slice'

let validText = /.*\S.*/

export default () => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let session = useSession()

  let [loading, setLoading] = useState(false)
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let courseID = useAppSelector(selectCourseID)
  let course = useAppSelector(selectCourseByID(courseID))
  let classID = useAppSelector(selectClassID)

  // this is necessary to ensure correct questionnaire flow on page refresh
  useEffect(() => {
    if (coursesStatus === 'uninitialized') {
      dispatch(fetchCourses())
    }
  }, [coursesStatus])

  let form = useFormik({
    validationSchema: yup.object({
      linkedServiceId: yup.string().nullable(),
      topicsThoroughAndInteresting: yup.number().required(),
      presenterGoodUnderstanding: yup.number().required(),
      videoQualityProfessional: yup.number().required(),
      benefitCareerGoals: yup.number().required(),
      knowledgePositiveImpactPractice: yup.number().required(),
      confusingTopics: yup.string().matches(validText).required(),
      thingsToBeContinued: yup.string().matches(validText).required(),
      thingsToBeImproved: yup.string().matches(validText).required(),
      explainImpactPractice: yup.string().matches(validText).required(),
    }),
    initialValues: {
      topicsThoroughAndInteresting: null,
      presenterGoodUnderstanding: null,
      videoQualityProfessional: null,
      benefitCareerGoals: null,
      knowledgePositiveImpactPractice: null,
      confusingTopics: null,
      thingsToBeContinued: null,
      thingsToBeImproved: null,
      explainImpactPractice: null,
      linkedServiceId: course?.linkedServiceID,
    },
    // eslint-disable-next-line fp/no-nil
    onSubmit: (values) => {
      // eslint-disable-next-line fp/no-unused-expression
      setLoading(true)
      axios
        .post('/api/v2/classroom/completed-ce', { CourseID: courseID })
        .then(({ data }) => data)
        .then((data) => {
          if (!course?.linkedServiceID) {
            axios.post('/api/v2/classroom/questionnaire', {
              SurveyID: data,
              CourseNumber: courseID,
              TopicsThoroughAndInteresting: values.topicsThoroughAndInteresting,
              PresenterGoodUnderstanding: values.presenterGoodUnderstanding,
              VideoQualityProfessional: values.videoQualityProfessional,
              BenefitCareerGoals: values.benefitCareerGoals,
              KnowledgePositiveImpactPractice:
                values.knowledgePositiveImpactPractice,
              ConfusingTopics: values.confusingTopics,
              ThingsToBeContinued: values.thingsToBeContinued,
              ThingsToBeImproved: values.thingsToBeImproved,
              ExplainImpactPractice: values.explainImpactPractice,
            })
          }
          return data
        })
        .then((data) => {
          navigate(`/classroom/certificate/${data}/${courseID}/${classID}`)
        })
    },
  })

  let submitLinkedServiceCE = () => {
    setLoading(true)
    axios
      .post('/api/v2/classroom/completed-ce', { CourseID: courseID })
      .then(({ data }) => data)
      .then((data) => {
        navigate(`/classroom/certificate/${data}/${courseID}/${classID}`)
      })
  }

  // eslint-disable-next-line fp/no-unused-expression,fp/no-nil
  useEffect(() => {
    if (session?.emailAddress) {
      // eslint-disable-next-line fp/no-unused-expression
      axios
        .get('/api/v2/classroom/user')
        .then(({ data }) =>
          Object.entries(data).forEach(([k, v]) =>
            k in form.values && complement(isNil)(v)
              ? form.setFieldValue(k, v)
              : noop,
          ),
        )
    }
  }, [session])

  return (
    <Box sx={{ m: 4, pt: 10 }}>
      <form onSubmit={form.handleSubmit}>
        {!course?.linkedServiceID ? (
          <>
            <Divider />
            <div className="mt-4">
              <Typography>
                For the next six questions please rate your response from a
                scale of 1 to 5 with 1 being worst and 5 being best.
              </Typography>

              <FormControl
                error={
                  form.touched.topicsThoroughAndInteresting &&
                  !!form.errors.topicsThoroughAndInteresting
                }
                component="fieldset"
                sx={{
                  mt: 4,
                }}
              >
                <FormLabel component="legend">
                  Were the topics presented in thorough and interesting manner?
                </FormLabel>
                <RadioGroup
                  aria-label="topicsThoroughAndInteresting"
                  name="topicsThoroughAndInteresting"
                  onChange={form.handleChange}
                  sx={{
                    mt: 4,
                    flexDirection: 'row',
                  }}
                >
                  <FormControlLabel value="1" control={<Radio />} label="1" />
                  <FormControlLabel value="2" control={<Radio />} label="2" />
                  <FormControlLabel value="3" control={<Radio />} label="3" />
                  <FormControlLabel value="4" control={<Radio />} label="4" />
                  <FormControlLabel value="5" control={<Radio />} label="5" />
                </RadioGroup>
              </FormControl>

              <FormControl
                error={
                  form.touched.presenterGoodUnderstanding &&
                  !!form.errors.presenterGoodUnderstanding
                }
                component="fieldset"
                className="w-full "
              >
                <FormLabel component="legend">
                  The presenter had a good understanding of the content being
                  delivered?
                </FormLabel>
                <RadioGroup
                  aria-label="presenterGoodUnderstanding"
                  name="presenterGoodUnderstanding"
                  onChange={form.handleChange}
                  sx={{
                    mt: 4,
                    flexDirection: 'row',
                  }}
                >
                  <FormControlLabel value="1" control={<Radio />} label="1" />
                  <FormControlLabel value="2" control={<Radio />} label="2" />
                  <FormControlLabel value="3" control={<Radio />} label="3" />
                  <FormControlLabel value="4" control={<Radio />} label="4" />
                  <FormControlLabel value="5" control={<Radio />} label="5" />
                </RadioGroup>
              </FormControl>

              <FormControl
                error={
                  form.touched.videoQualityProfessional &&
                  !!form.errors.videoQualityProfessional
                }
                component="fieldset"
                className="w-full"
              >
                <FormLabel component="legend">
                  The video quality was professional?
                </FormLabel>
                <RadioGroup
                  aria-label="videoQualityProfessional"
                  name="videoQualityProfessional"
                  onChange={form.handleChange}
                  sx={{
                    mt: 4,
                    flexDirection: 'row',
                  }}
                >
                  <FormControlLabel value="1" control={<Radio />} label="1" />
                  <FormControlLabel value="2" control={<Radio />} label="2" />
                  <FormControlLabel value="3" control={<Radio />} label="3" />
                  <FormControlLabel value="4" control={<Radio />} label="4" />
                  <FormControlLabel value="5" control={<Radio />} label="5" />
                </RadioGroup>
              </FormControl>

              <FormControl
                error={
                  form.touched.benefitCareerGoals &&
                  !!form.errors.benefitCareerGoals
                }
                component="fieldset"
                className="w-full"
              >
                <FormLabel component="legend">
                  The materials presented will benefit me in my career goals
                </FormLabel>
                <RadioGroup
                  aria-label="benefitCareerGoals"
                  name="benefitCareerGoals"
                  onChange={form.handleChange}
                  sx={{
                    mt: 4,
                    flexDirection: 'row',
                  }}
                >
                  <FormControlLabel value="1" control={<Radio />} label="1" />
                  <FormControlLabel value="2" control={<Radio />} label="2" />
                  <FormControlLabel value="3" control={<Radio />} label="3" />
                  <FormControlLabel value="4" control={<Radio />} label="4" />
                  <FormControlLabel value="5" control={<Radio />} label="5" />
                </RadioGroup>
              </FormControl>

              <FormControl
                error={
                  form.touched.knowledgePositiveImpactPractice &&
                  !!form.errors.knowledgePositiveImpactPractice
                }
                component="fieldset"
                className="w-full"
              >
                <FormLabel component="legend">
                  How strongly do you believe the knowledge gained from this
                  educational activity will positively impact your practice?
                </FormLabel>
                <RadioGroup
                  aria-label="knowledgePositiveImpactPractice"
                  name="knowledgePositiveImpactPractice"
                  onChange={form.handleChange}
                  sx={{
                    mt: 4,
                    flexDirection: 'row',
                  }}
                >
                  <FormControlLabel value="1" control={<Radio />} label="1" />
                  <FormControlLabel value="2" control={<Radio />} label="2" />
                  <FormControlLabel value="3" control={<Radio />} label="3" />
                  <FormControlLabel value="4" control={<Radio />} label="4" />
                  <FormControlLabel value="5" control={<Radio />} label="5" />
                </RadioGroup>
              </FormControl>

              <TextField
                name="confusingTopics"
                error={
                  form.touched.confusingTopics && !!form.errors.confusingTopics
                }
                label="Are there any topics that still confuse you?"
                multiline
                variant="standard"
                className="w-full my-3"
                sx={{
                  mt: 4,
                  textarea: {
                    resize: 'both',
                    width: '100%',
                  },
                }}
                onChange={form.handleChange}
              />

              <TextField
                name="thingsToBeContinued"
                error={
                  form.touched.thingsToBeContinued &&
                  !!form.errors.thingsToBeContinued
                }
                label="Likes/strengths/things to be continued?"
                multiline
                variant="standard"
                className="w-full my-3"
                sx={{
                  mt: 4,
                  textarea: {
                    resize: 'both',
                    width: '100%',
                  },
                }}
                onChange={form.handleChange}
              />

              <TextField
                name="thingsToBeImproved"
                error={
                  form.touched.thingsToBeImproved &&
                  !!form.errors.thingsToBeImproved
                }
                label="Dislikes/weaknesses/things to be changed or improved?"
                multiline
                variant="standard"
                className="w-full my-3"
                sx={{
                  mt: 4,
                  textarea: {
                    resize: 'both',
                    width: '100%',
                  },
                }}
                onChange={form.handleChange}
              />

              <TextField
                name="explainImpactPractice"
                error={
                  form.touched.explainImpactPractice &&
                  !!form.errors.explainImpactPractice
                }
                label="Discuss three things you learned during this educational activity and explain how at least one of them will impact your future practice."
                multiline
                variant="standard"
                className="w-full my-3"
                sx={{
                  mt: 4,
                  textarea: {
                    resize: 'both',
                    width: '100%',
                  },
                }}
                onChange={form.handleChange}
              />
            </div>
            <div className="flex justify-end">
              <Button
                variant="contained"
                color="primary"
                type="submit"
                className="mt-4 mb-2 mr-3"
              >
                Submit
              </Button>
            </div>
          </>
        ) : (
          <>
            <Box
              sx={{
                position: 'absolute',
                opacity: 0.5,
                backgroundImage: `url(${import.meta.env.VITE_CLOUDFRONT_URL}/public/${course?.imageName})`,
                backgroundSize: 'cover',
                width: '100%',
                height: 'calc(100vh - 64px)',
              }}
            ></Box>
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
              }}
            >
              <Card
                sx={{
                  mt: 10,
                  py: 2,
                  px: 3,
                  border: '2px solid primary.dark',
                  boxShadow: '5px 5px 5px background.paper',
                  borderRadius: 5,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <CardContent sx={{ py: 1 }}>
                  <Typography
                    textAlign="center"
                    variant="h4"
                    sx={{ fontFamily: 'smileAndWave' }}
                  >
                    {course?.name}
                  </Typography>
                  <Typography>
                    There is no survey for this course. You may now continue to
                    get your certificate.
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => submitLinkedServiceCE()}
                  >
                    Generate Certificate
                  </Button>
                </CardActions>
              </Card>
            </Box>
          </>
        )}
      </form>
      <div>
        <Backdrop
          sx={{
            backdrop: {
              zIndex: 'theme.zindex.drawer',
              color: '#fff',
            },
          }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
    </Box>
  )
}
